import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Users, Play, Square } from 'lucide-react';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';
import SetupTab from './SetupTab';
import ManageTab from './ManageTab';
import SettingsTab from './SettingsTab';

const TABS = [
  { key: 'setup', label: 'Setup' },
  { key: 'manage', label: 'Manage' },
  { key: 'settings', label: 'Settings' },
];

export default function BreakoutRoomModal({ isOpen, onClose, participants = [] }) {
  const [activeTab, setActiveTab] = useState('setup');
  const {
    session,
    rooms,
    isActive,
    isConnected,
    openRooms,
    closeRooms,
    createSession,
    refreshSession,
    assignedParticipantIds,
  } = useBreakoutRooms();

  // Fetch latest session state from server whenever modal opens
  useEffect(() => {
    if (isOpen) {
      refreshSession?.();
    }
  }, [isOpen, refreshSession]);

  // Retry session refresh while modal is open and session is null (handles socket reconnection timing)
  useEffect(() => {
    if (!isOpen || session) return;
    const interval = setInterval(() => {
      refreshSession?.();
    }, 1500);
    return () => clearInterval(interval);
  }, [isOpen, session, refreshSession]);

  // Auto-create session when modal opens (if no session exists and not already active)
  const sessionCreated = useRef(false);
  useEffect(() => {
    if (isOpen && !session && !sessionCreated.current) {
      sessionCreated.current = true;
      createSession();
    }
    if (!isOpen) {
      sessionCreated.current = false;
    }
  }, [isOpen, session, createSession]);

  // Auto-switch to Manage tab when session becomes active, and back to Setup when closed
  useEffect(() => {
    if (isActive) {
      setActiveTab('manage');
    } else if (session?.status === 'closed') {
      setActiveTab('setup');
    }
  }, [isActive, session?.status]);

  // Track local assignment count from SetupTab (updates immediately, before server sync)
  const [localAssignedCount, setLocalAssignedCount] = useState(0);
  const handleAssignmentChange = useCallback((count) => {
    setLocalAssignedCount(count);
  }, []);

  if (!isOpen) return null;

  // participants are already resolved objects with { id, displayName, name, role, isLocal }
  // Exclude hosts/moderators/co-hosts and local user from total (they can't be assigned)
  const assignableParticipants = participants.filter(p =>
    !p.isLocal && !['host', 'moderator', 'co-host'].includes(p.role)
  );
  const totalParticipants = assignableParticipants.length;
  const serverAssignedCount = assignedParticipantIds?.size || 0;
  // Use the higher of local vs server count so button enables immediately after auto-assign
  const assignedCount = Math.max(localAssignedCount, serverAssignedCount);

  return (
    <div
      // items-end on mobile docks the modal to the bottom of the viewport so
      // the iOS Safari URL bar can't push the footer (Open Rooms / Cancel) or
      // the close button out of view. Centered on sm+ as before.
      // Use 100dvh — dynamic viewport height respects mobile browser chrome,
      // unlike vh which is frozen to the largest viewport size.
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      <div
        className="bg-[#001233] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-5xl h-[100dvh] sm:h-auto sm:max-h-[85dvh] flex flex-col sm:mx-4"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/10">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base md:text-xl font-bold text-white truncate">Breakout Rooms</h2>
              <p className="text-xs md:text-sm text-gray-400 truncate">
                {isActive ? 'Session in progress' : 'Create and manage breakout sessions'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tab Bar */}
        <div className="mx-4 md:mx-6 mt-3 md:mt-4">
          <div className="bg-white/5 rounded-lg p-1 flex gap-1 w-full md:w-fit">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 md:flex-none px-4 md:px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white/10 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content — always scroll on mobile so nothing gets clipped.
            Setup tab keeps overflow-hidden on md+ where the inner two-column
            flex layout manages its own scroll regions; on mobile the column
            stack can exceed viewport height and must scroll at this level. */}
        <div className={`flex-1 p-4 md:p-6 pt-3 md:pt-4 min-h-0 overflow-y-auto ${activeTab === 'setup' ? 'md:overflow-hidden' : ''}`}>
          {activeTab === 'setup' && (
            <SetupTab participants={participants} onAssignmentChange={handleAssignmentChange} />
          )}
          {activeTab === 'manage' && (
            <ManageTab />
          )}
          {activeTab === 'settings' && (
            <SettingsTab />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-white/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
          {isActive ? (
            <>
              <div className="flex items-center gap-2 text-green-400 justify-center sm:justify-start">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                <span className="text-sm">Breakout session active</span>
              </div>
              <button
                onClick={() => { closeRooms(); }}
                className="px-4 md:px-6 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-700 transition-colors"
              >
                <Square className="w-4 h-4" />
                End All Rooms
              </button>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-400 text-center sm:text-left">
                {assignedCount} of {totalParticipants} participants assigned
              </div>
              <div className="flex gap-3 justify-stretch sm:justify-end">
                <button
                  onClick={onClose}
                  className="flex-1 sm:flex-none px-4 py-2.5 border border-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { openRooms(); }}
                  disabled={assignedCount === 0}
                  className="flex-1 sm:flex-none px-4 md:px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <Play className="w-4 h-4 flex-shrink-0" />
                  Open Rooms
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
